import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ASSETS, BRAND, PROGRAMS } from "@/lib/brand";
import { ThemeToggle } from "@/components/ThemeToggle";

const GOALS = [
  "More energy",
  "Feel more confident",
  "Hormone balance",
  "Gut health",
  "Build lean muscle",
  "Tone",
  "Lose weight",
  "Heal my relationship with food",
  "Get off the diet rollercoaster",
];

const STEPS = [
  { key: "welcome", label: "Welcome" },
  { key: "contact", label: "About you" },
  { key: "program", label: "The path" },
  { key: "goals", label: "Your goals" },
  { key: "story", label: "Your story" },
  { key: "commit", label: "Commitment" },
  { key: "done", label: "Sent" },
];

export default function Apply() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", age: "", instagram: "", phone: "",
    program_interest: "not_sure",
    goals: [],
    top_goal: "", erase_three: "",
    past_diets: "", current_exercise: "",
    hormone_status: "", hormone_details: "",
    biggest_hurdle: "", missed_experiences: "", why: "", looking_for: "",
    commitment_level: 8, timeline: "", budget_aware: false,
    referral: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setRaw = (k, v) => setForm({ ...form, [k]: v });
  const toggleGoal = (g) => {
    setForm({
      ...form,
      goals: form.goals.includes(g) ? form.goals.filter((x) => x !== g) : [...form.goals, g],
    });
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        hormone_status: form.hormone_status || null,
        program_interest: form.program_interest || "not_sure",
      };
      await api.post("/leads", payload);
      setStep(STEPS.length - 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const stepIndex = step;
  const isLast = step === STEPS.length - 1;
  const isWelcome = step === 0;
  const totalForms = STEPS.length - 2; // exclude welcome + done

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12" style={{ background: "var(--bg)" }}>
      {/* Left visual rail */}
      <aside className="hidden lg:flex lg:col-span-5 relative overflow-hidden" style={{ background: "var(--ink)" }}>
        <img src={ASSETS.brandedBodysuit} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(26,23,20,0.05) 0%, rgba(26,23,20,0.75) 100%)" }} />
        <div className="absolute top-8 left-8 right-8 z-10 flex justify-between items-center">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-script text-3xl text-white">{BRAND.name}</span>
            <span className="wordmark-tag" style={{ color: "#FFFFFF" }}>{BRAND.tag}</span>
          </Link>
          <ThemeToggle />
        </div>
        <div className="relative z-10 mt-auto p-12 text-white">
          <p className="overline" style={{ color: "var(--accent)" }}>The application</p>
          <h2 className="font-display text-4xl mt-6 leading-tight">
            Let&apos;s do this,<br />
            <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>beautiful.</span>
          </h2>
          <div className="bronze-rule mt-6" />
          <p className="font-soft text-lg mt-8 max-w-md leading-relaxed">
            I coach women all over the world and I take this role seriously.
            These questions help me understand you before our call — but mostly,
            I&apos;m excited to meet you.
          </p>
        </div>
      </aside>

      {/* Right form */}
      <main className="lg:col-span-7 flex flex-col px-6 md:px-12 lg:px-20 py-8 lg:py-12 min-h-screen">
        <div className="lg:hidden flex justify-between items-center mb-8">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-script text-2xl">{BRAND.name}</span>
            <span className="wordmark-tag">{BRAND.tag}</span>
          </Link>
          <ThemeToggle />
        </div>

        {/* Progress dots */}
        {!isWelcome && !isLast && (
          <div className="flex items-center gap-2 mb-10" data-testid="apply-progress">
            {STEPS.slice(1, -1).map((s, i) => (
              <div key={s.key} className="flex-1 h-px transition-colors"
                style={{ background: i + 1 <= stepIndex ? "var(--accent)" : "var(--border-strong)" }} />
            ))}
            <span className="font-label text-[10px] tracking-[0.22em] uppercase ml-3" style={{ color: "var(--text-tertiary)" }}>
              {Math.min(stepIndex, totalForms)} / {totalForms}
            </span>
          </div>
        )}

        <div className="flex-1 max-w-2xl mx-auto w-full">
          {/* Step 0 — Welcome */}
          {step === 0 && (
            <div data-testid="apply-step-welcome" className="fade-up">
              <p className="overline">Apply</p>
              <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
                Begin your<br />
                <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>restoration.</span>
              </h1>
              <div className="bronze-rule mt-6" />
              <p className="font-soft text-xl mt-8 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                This application takes about 5 minutes. There are no wrong answers —
                just dig deep and tell me the truth. The clarity is for both of us.
              </p>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                {[PROGRAMS.mtm, PROGRAMS.riseWithin].map((p) => (
                  <div key={p.key} className="border p-6" style={{ borderColor: "var(--border-strong)" }}>
                    <div className="overline">{p.type}</div>
                    <div className="font-display text-lg mt-3 leading-tight">{p.title}</div>
                    <div className="font-label text-[10px] tracking-widest uppercase mt-2" style={{ color: "var(--text-tertiary)" }}>{p.duration}</div>
                  </div>
                ))}
              </div>
              <button onClick={next} className="btn-primary mt-12" data-testid="apply-begin">
                Begin the application <ArrowRight size={12} />
              </button>
              <p className="font-label text-[11px] tracking-wider mt-6" style={{ color: "var(--text-secondary)" }}>
                Already have an account? <Link to="/login" className="text-accent">Sign in</Link>
              </p>
            </div>
          )}

          {/* Step 1 — Contact */}
          {step === 1 && (
            <div data-testid="apply-step-contact" className="fade-up">
              <p className="overline">01 · About you</p>
              <h2 className="font-display text-3xl mt-5">Let&apos;s start with the basics.</h2>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-7">
                <div><label className="label-luxe">First name *</label>
                  <input className="input-luxe" required value={form.name} onChange={set("name")} data-testid="apply-name" /></div>
                <div><label className="label-luxe">Email *</label>
                  <input type="email" className="input-luxe" required value={form.email} onChange={set("email")} data-testid="apply-email" /></div>
                <div><label className="label-luxe">Age *</label>
                  <input className="input-luxe" required value={form.age} onChange={set("age")} data-testid="apply-age" /></div>
                <div><label className="label-luxe">Instagram handle *</label>
                  <input className="input-luxe" required value={form.instagram} onChange={set("instagram")} placeholder="@yourhandle" data-testid="apply-instagram" /></div>
                <div className="md:col-span-2"><label className="label-luxe">Phone (optional)</label>
                  <input className="input-luxe" value={form.phone} onChange={set("phone")} /></div>
              </div>
              <NextBack busy={busy} canNext={form.name && form.email && form.age && form.instagram} onBack={back} onNext={next} />
            </div>
          )}

          {/* Step 2 — Program */}
          {step === 2 && (
            <div data-testid="apply-step-program" className="fade-up">
              <p className="overline">02 · The path</p>
              <h2 className="font-display text-3xl mt-5">Which program are you leaning toward?</h2>
              <div className="bronze-rule mt-5" />
              <p className="font-soft text-base mt-5" style={{ color: "var(--text-secondary)" }}>
                Knowing where you&apos;re leaning helps us make the most of your call. And you can
                always choose &quot;help me decide&quot; — we&apos;ll find the right fit together.
              </p>
              <div className="mt-8 space-y-3">
                {[
                  { v: "mtm", t: PROGRAMS.mtm.title, s: `${PROGRAMS.mtm.type} · ${PROGRAMS.mtm.duration}`, b: "The signature group program. Four phases. Community + structure." },
                  { v: "rise_within", t: PROGRAMS.riseWithin.title, s: `${PROGRAMS.riseWithin.type} · By application`, b: "Bespoke 1:1. Lab testing, customized protocols, weekly check-ins with DJ." },
                  { v: "not_sure", t: "Help me decide", s: "Schedule a call", b: "We'll figure it out together — I'll recommend the right fit on our 30-min discovery call." },
                ].map((opt) => (
                  <button key={opt.v} type="button" onClick={() => setRaw("program_interest", opt.v)}
                    data-testid={`apply-program-${opt.v}`}
                    className="w-full text-left p-6 border transition-colors"
                    style={{
                      borderColor: form.program_interest === opt.v ? "var(--accent)" : "var(--border-strong)",
                      background: form.program_interest === opt.v ? "rgba(168,152,128,0.08)" : "transparent",
                    }}>
                    <div className="flex items-start gap-4">
                      <div className="w-5 h-5 rounded-full border flex-shrink-0 mt-0.5 flex items-center justify-center"
                        style={{ borderColor: form.program_interest === opt.v ? "var(--accent)" : "var(--border-strong)" }}>
                        {form.program_interest === opt.v && <div className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--accent)" }} />}
                      </div>
                      <div className="flex-1">
                        <div className="font-display text-lg leading-tight">{opt.t}</div>
                        <div className="font-label text-[10px] tracking-widest uppercase mt-1" style={{ color: "var(--accent)" }}>{opt.s}</div>
                        <p className="font-soft text-base mt-2" style={{ color: "var(--text-secondary)" }}>{opt.b}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <NextBack busy={busy} onBack={back} onNext={next} />
            </div>
          )}

          {/* Step 3 — Goals */}
          {step === 3 && (
            <div data-testid="apply-step-goals" className="fade-up">
              <p className="overline">03 · Your goals</p>
              <h2 className="font-display text-3xl mt-5">What are you here for?</h2>
              <div className="mt-8">
                <label className="label-luxe">Health &amp; fitness goals (select all that apply) *</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                  {GOALS.map((g) => (
                    <button key={g} type="button" onClick={() => toggleGoal(g)}
                      data-testid={`apply-goal-${g.replace(/\s/g, '-').toLowerCase()}`}
                      className="px-3 py-2.5 text-left font-label text-[11px] tracking-[0.14em] uppercase border transition-colors"
                      style={{
                        borderColor: form.goals.includes(g) ? "var(--accent)" : "var(--border-strong)",
                        color: form.goals.includes(g) ? "var(--accent)" : "var(--text-secondary)",
                        background: form.goals.includes(g) ? "rgba(168,152,128,0.08)" : "transparent",
                      }}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-8"><label className="label-luxe">If you could erase 3 problems related to your health, what would they be? What is your top goal? *</label>
                <textarea className="input-luxe" rows={4} required value={form.top_goal} onChange={set("top_goal")} data-testid="apply-top-goal" /></div>
              <div className="mt-7"><label className="label-luxe">What diets have you tried in the past?</label>
                <textarea className="input-luxe" rows={3} value={form.past_diets} onChange={set("past_diets")} data-testid="apply-diets" /></div>
              <div className="mt-7"><label className="label-luxe">What are you currently doing for exercise?</label>
                <textarea className="input-luxe" rows={3} value={form.current_exercise} onChange={set("current_exercise")} data-testid="apply-exercise" /></div>

              <div className="mt-7">
                <label className="label-luxe">Have you been diagnosed with a hormone imbalance?</label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {[
                    { v: "yes", t: "Yes" },
                    { v: "no", t: "No" },
                    { v: "suspect", t: "Something feels off" },
                  ].map((o) => (
                    <button key={o.v} type="button" onClick={() => setRaw("hormone_status", o.v)}
                      data-testid={`apply-hormone-${o.v}`}
                      className="px-3 py-3 font-label text-[10px] tracking-[0.18em] uppercase border"
                      style={{
                        borderColor: form.hormone_status === o.v ? "var(--accent)" : "var(--border-strong)",
                        color: form.hormone_status === o.v ? "var(--accent)" : "var(--text-secondary)",
                        background: form.hormone_status === o.v ? "rgba(168,152,128,0.08)" : "transparent",
                      }}>{o.t}</button>
                  ))}
                </div>
              </div>
              {(form.hormone_status === "yes" || form.hormone_status === "suspect") && (
                <div className="mt-5"><label className="label-luxe">Tell me more</label>
                  <textarea className="input-luxe" rows={3} value={form.hormone_details} onChange={set("hormone_details")} /></div>
              )}
              <NextBack busy={busy} canNext={form.goals.length > 0 && form.top_goal} onBack={back} onNext={next} />
            </div>
          )}

          {/* Step 4 — Story */}
          {step === 4 && (
            <div data-testid="apply-step-story" className="fade-up">
              <p className="overline">04 · Your story</p>
              <h2 className="font-display text-3xl mt-5">Let&apos;s dig a little deeper.</h2>
              <div className="bronze-rule mt-5" />
              <div className="mt-8 space-y-7">
                <div><label className="label-luxe">What is the biggest hurdle you haven&apos;t been able to overcome?</label>
                  <textarea className="input-luxe" rows={3} value={form.biggest_hurdle} onChange={set("biggest_hurdle")} data-testid="apply-hurdle" /></div>
                <div><label className="label-luxe">What experiences are you missing out on right now?</label>
                  <textarea className="input-luxe" rows={3} value={form.missed_experiences} onChange={set("missed_experiences")} data-testid="apply-missing" /></div>
                <div>
                  <label className="label-luxe">Your WHY *</label>
                  <p className="font-soft text-base mt-1" style={{ color: "var(--text-tertiary)" }}>
                    Why do you want to achieve these goals? How would your life, relationships, day be different? Dig deep — it&apos;s normal to get emotional.
                  </p>
                  <textarea className="input-luxe mt-3" rows={5} required value={form.why} onChange={set("why")} data-testid="apply-why" /></div>
                <div><label className="label-luxe">What are you looking for in a coach?</label>
                  <textarea className="input-luxe" rows={3} value={form.looking_for} onChange={set("looking_for")} data-testid="apply-looking-for" /></div>
              </div>
              <NextBack busy={busy} canNext={form.why} onBack={back} onNext={next} />
            </div>
          )}

          {/* Step 5 — Commitment */}
          {step === 5 && (
            <div data-testid="apply-step-commit" className="fade-up">
              <p className="overline">05 · Commitment</p>
              <h2 className="font-display text-3xl mt-5">Where are you, honestly?</h2>
              <div className="mt-8">
                <label className="label-luxe">On a scale of 1–10, how committed are you to achieving these goals? *</label>
                <div className="grid grid-cols-10 gap-1 mt-4">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <button key={n} type="button" onClick={() => setRaw("commitment_level", n)}
                      data-testid={`apply-commit-${n}`}
                      className="aspect-square font-label text-sm border transition-colors"
                      style={{
                        borderColor: form.commitment_level === n ? "var(--accent)" : "var(--border-strong)",
                        background: form.commitment_level === n ? "var(--accent)" : "transparent",
                        color: form.commitment_level === n ? "#FFFFFF" : "var(--text-secondary)",
                      }}>{n}</button>
                  ))}
                </div>
                <div className="flex justify-between mt-3 font-label text-[10px] tracking-wider uppercase" style={{ color: "var(--text-tertiary)" }}>
                  <span>I&apos;m okay where I am</span>
                  <span>Ready &amp; committed</span>
                </div>
              </div>
              <div className="mt-8">
                <label className="label-luxe">When do you want to start?</label>
                <select className="input-luxe" value={form.timeline} onChange={set("timeline")} data-testid="apply-timeline">
                  <option value="">Select…</option>
                  <option>Immediately</option>
                  <option>Within 1 month</option>
                  <option>Within 3 months</option>
                  <option>Just exploring</option>
                </select>
              </div>
              <div className="mt-8">
                <label className="label-luxe">How did you find me? (optional)</label>
                <input className="input-luxe" value={form.referral} onChange={set("referral")} placeholder="Instagram, friend, podcast…" />
              </div>
              <div className="mt-8 flex items-start gap-3 cursor-pointer" onClick={() => setRaw("budget_aware", !form.budget_aware)}>
                <div className="w-5 h-5 border flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ borderColor: form.budget_aware ? "var(--accent)" : "var(--border-strong)", background: form.budget_aware ? "var(--accent)" : "transparent" }}>
                  {form.budget_aware && <Check size={12} className="text-white" />}
                </div>
                <p className="font-soft text-base" style={{ color: "var(--text-secondary)" }}>
                  I understand premium coaching is an investment in myself, and I&apos;m ready to discuss
                  the right path on our call. (Payment plans are available.)
                </p>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <button onClick={back} className="btn-ghost" data-testid="apply-back">
                  <ArrowLeft size={12} /> Back
                </button>
                <button onClick={submit} disabled={busy || !form.commitment_level} className="btn-primary" data-testid="apply-submit">
                  {busy ? "Sending…" : "Send my application"} <ArrowRight size={12} />
                </button>
              </div>
            </div>
          )}

          {/* Done */}
          {step === STEPS.length - 1 && (
            <div data-testid="apply-step-done" className="text-center max-w-xl mx-auto fade-up">
              <p className="overline">Received</p>
              <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
                Thank you,<br />
                <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>beautiful.</span>
              </h2>
              <div className="bronze-rule mx-auto mt-8" />
              <p className="font-soft text-xl mt-10 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                Your application is in my inbox. The next step is our 30-minute
                strategy call — book a time that works for you below.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 justify-center">
                <a href={BRAND.calendlyUrl} target="_blank" rel="noreferrer" className="btn-primary" data-testid="apply-book-call">
                  Book my strategy call <ArrowRight size={12} />
                </a>
                <button onClick={() => navigate("/")} className="btn-secondary">Back to home</button>
              </div>
              <p className="font-soft text-base mt-12 italic" style={{ color: "var(--text-tertiary)" }}>
                In the meantime — drink some water, take a deep breath, and start noticing.
                The work has already begun.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function NextBack({ busy, canNext = true, onBack, onNext }) {
  return (
    <div className="mt-12 flex items-center gap-4">
      <button onClick={onBack} disabled={busy} className="btn-ghost" data-testid="apply-back">
        <ArrowLeft size={12} /> Back
      </button>
      <button onClick={onNext} disabled={busy || !canNext} className="btn-primary" data-testid="apply-next">
        Continue <ArrowRight size={12} />
      </button>
    </div>
  );
}
